import { branding } from '@fsd/shared';
import { Badge, InstagramIcon, WhatsAppIcon } from '@fsd/ui';
import { Award, Clock, Mail, Phone, Shield } from 'lucide-react';
import attoLogoAccent from '@/assets/brands/attodev-logo-accent.svg';
import attoLogoDark from '@/assets/brands/attodev-logo-dark.svg';
import { useTranslation } from '@/i18n/hooks';
import { scrollToSection } from '@/utils';

const attoWebsiteUrl = 'https://attodev.com.br';

export function Footer() {
  const { t } = useTranslation('home');

  const currentYear = new Date().getFullYear();

  // Format hours from branding with translations
  const formattedHours = `${t('footer.contact.hours.weekdays', {
    start: branding.contact.hours.weekdays.start,
    end: branding.contact.hours.weekdays.end,
  })}\n${t('footer.contact.hours.saturday', {
    start: branding.contact.hours.saturday.start,
    end: branding.contact.hours.saturday.end,
  })}`;

  const navigationLinks = [
    { name: t('footer.links.home'), href: '#start' },
    { name: t('footer.links.newDrones'), href: '#new-drones' },
    { name: t('footer.links.usedDrones'), href: '#used-drones' },
    { name: t('footer.links.services'), href: '#maintenance' },
    { name: t('footer.links.about'), href: '#about' },
    { name: t('footer.links.contact'), href: '#contact' },
  ];

  return (
    <footer className="w-full border-t border-slate-200/50 bg-white dark:border-slate-700/50 dark:bg-slate-900">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:py-16">
        <div className="grid grid-cols-1 gap-8 sm:gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Company Info - Takes 2 columns on large screens */}
          <div className="lg:col-span-2">
            <h3 className="mb-4 text-2xl font-bold text-slate-900 dark:text-slate-100">
              {t('footer.company.name')}
            </h3>
            <p className="mb-6 leading-relaxed text-slate-600 dark:text-slate-400">
              {t('footer.company.description')}
            </p>

            {/* Badges */}
            <div className="mb-6 flex flex-wrap gap-3 sm:gap-4">
              <Badge
                variant="primary"
                size="lg"
                icon={<Award className="h-4 w-4" />}
                className="rounded-lg ring-1 ring-primary/20 dark:ring-primary/30"
              >
                {t('footer.badges.experience')}
              </Badge>
              <Badge
                variant="primary"
                size="lg"
                icon={<Shield className="h-4 w-4" />}
                className="rounded-lg ring-1 ring-primary/20 dark:ring-primary/30"
              >
                {t('footer.badges.quality')}
              </Badge>
            </div>

            {/* Social Media */}
            <div className="flex gap-4">
              <a
                href={branding.contact.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-all hover:bg-[#20BA5A] hover:shadow-xl"
                aria-label="WhatsApp"
              >
                <WhatsAppIcon className="h-5 w-5" />
              </a>
              <a
                href={branding.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-linear-to-r from-purple-500 to-pink-500 text-white shadow-lg transition-all hover:from-purple-600 hover:to-pink-600 hover:shadow-xl"
                aria-label="Instagram"
              >
                <InstagramIcon className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links - Hidden on mobile */}
          <div className="hidden md:block">
            <h4 className="mb-6 text-lg font-semibold text-slate-900 dark:text-slate-100">
              {t('footer.sections.quickLinks')}
            </h4>
            <ul className="space-y-3">
              {navigationLinks.map((link) => (
                <li key={link.href}>
                  <button
                    type="button"
                    onClick={() => scrollToSection(link.href)}
                    className="text-left text-slate-600 transition-colors hover:text-primary dark:text-slate-400 dark:hover:text-primary"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info - Hidden on mobile */}
          <div className="hidden md:block">
            <h4 className="mb-6 text-lg font-semibold text-slate-900 dark:text-slate-100">
              {t('footer.sections.contact')}
            </h4>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 shrink-0 text-primary" />
                <a
                  href={`tel:${branding.contact.phone}`}
                  className="text-sm text-slate-600 transition-colors hover:text-primary dark:text-slate-400 dark:hover:text-primary break-all"
                >
                  {branding.contact.phone}
                </a>
              </div>

              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 shrink-0 text-primary" />
                <a
                  href={`mailto:${branding.contact.email}`}
                  className="text-sm text-slate-600 transition-colors hover:text-primary dark:text-slate-400 dark:hover:text-primary break-all"
                >
                  {branding.contact.email}
                </a>
              </div>

              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 shrink-0 text-primary" />
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  <p className="whitespace-pre-line">{formattedHours}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-200/50 bg-slate-50 dark:border-slate-700/50 dark:bg-slate-800">
        <div className="mx-auto max-w-7xl px-4 py-6">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p className="text-sm text-center text-slate-600 dark:text-slate-400">
              {t('footer.copyright', { year: currentYear })}
            </p>
            <a
              href={attoWebsiteUrl}
              className="group flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
              aria-label={t('footer.madeBy.ariaLabel')}
            >
              <span>{t('footer.madeBy.label')}</span>
              <span className="flex h-5 w-[102px] items-center">
                <img
                  src={attoLogoAccent}
                  alt="AttoDev"
                  className="block h-full w-full object-contain transition-opacity group-hover:opacity-80 dark:hidden"
                />
                <img
                  src={attoLogoDark}
                  alt="AttoDev"
                  className="hidden h-full w-full object-contain transition-opacity group-hover:opacity-80 dark:block"
                />
              </span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
